import { Box, Typography } from '@mui/material';

interface TokenUsageProps {
  used: number;
  limit: number;
  history: { month: string; used: number }[];
}

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}М` : `${Math.round(n / 1000)}К`;

export default function TokenUsage({ used, limit, history }: TokenUsageProps) {
  const pct = Math.round((used / limit) * 100);
  const r = 26;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid rgba(20,116,255,0.10)',
        boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
        borderRadius: 'var(--radius-lg)',
        p: 2.5,
        transition: 'box-shadow 0.20s ease',
        '&:hover': { boxShadow: '0 4px 24px rgba(20,116,255,0.12), 0 1px 4px rgba(0,0,0,0.04)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--color-text-muted)',
          }}
        >
          Токены
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <svg width="64" height="64" viewBox="0 0 64 64" style={{ flexShrink: 0 }}>
          <circle
            cx="32"
            cy="32"
            r={r}
            fill="none"
            stroke="rgba(20,116,255,0.10)"
            strokeWidth="10"
          />
          <circle
            cx="32"
            cy="32"
            r={r}
            fill="none"
            stroke="#1474FF"
            strokeWidth="10"
            strokeDasharray={`${circ} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 32 32)"
          />
          <text x="32" y="37" textAnchor="middle" fontSize="11" fontWeight="800" fill="#1474FF">
            {pct}%
          </text>
        </svg>
        <Box>
          <Typography
            sx={{ fontSize: 20, fontWeight: 800, color: 'var(--color-primary)', lineHeight: 1 }}
          >
            {fmt(used)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            из {fmt(limit)} лимита
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: 'var(--color-success)', fontWeight: 600, mt: 0.25 }}
          >
            ↑ {fmt(limit - used)} в запасе
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {history.map((h) => (
          <Box key={h.month}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {h.month}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                {fmt(h.used)}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 5,
                background: 'rgba(20,116,255,0.08)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '3px',
                  width: `${(h.used / limit) * 100}%`,
                  background: 'linear-gradient(90deg, #1474FF, #3B8BFF)',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
